import type { Greathelm } from "../../types/lib/index.ts";
import { Lookup } from "../oss/Lookup";
import { ArmorProfile } from "./ArmorProfile";
import { DefenseProfile } from "./DefenseProfile";
import { ItemProfile } from "./ItemProfile";
import { KnightProfile } from "./KnightProfile";
import { ShieldProfile } from "./ShieldProfile";
import { UpgradeProfile } from "./UpgradeProfile";
import { WeakMapCache } from "./WeakMapCache";
import { WeaponProfile } from "./WeaponProfile";

export type Ref = {
  id: string;
};

export type AddDefenseModifierEffect = {
  type: "ADD_DEFENSE_MODIFIER";
  defense: Ref | Ref[];
  value: number;
};

export type AddActionModifierEffect = {
  type: "ADD_ACTION_MODIFIER";
  action: Ref | Ref[];
  value: number;
};

export type AddMomentumLimitModifierEffect = {
  type: "ADD_MOMENTUM_LIMIT_MODIFIER";
  value: number;
};

export type GainAction = {
  type: "GAIN_ACTION";
  action: Ref;
};

export type RemoveAction = {
  type: "REMOVE_ACTION";
  action: Ref;
};

export type RemoveDefense = {
  type: "REMOVE_DEFENSE";
  defense: Ref | Ref[];
};

export type AddSpecialRule = {
  type: "ADD_SPECIAL_RULE";
  group: "PASSIVE" | "ATTACK" | "ARMOR" | "DEFENSE" | "USEABLE";
  value: string;
  hideInSummary?: boolean;
};

export type EffectData =
  | AddSpecialRule
  | GainAction
  | RemoveDefense
  | RemoveAction
  | AddActionModifierEffect
  | AddDefenseModifierEffect
  | AddMomentumLimitModifierEffect;

export type NonArray<T> = T extends Array<unknown> ? never : T;

export type EffectMap = {
  [K in EffectData["type"]]: Extract<EffectData, { type: K }>;
};

export type WeaponTraitData = {
  id: string;
  name: string;
  description: string;
  effects?: EffectData[];
};

export type WeaponProfileData = {
  id: string;
  name: string;
  type: "ONE_HANDED" | "TWO_HANDED" | "TWO_HANDED_RANGED";
  traits?: Ref[];
  effects?: EffectData[];
};

export type KnightProfileData = {
  id: string;
  name: string;
  damageLimit: number;

  /**
   * If not present, any armor can be chosen.
   */
  armorOptions?: Ref[];

  /**
   * If not present, any weapon can be chosen.
   */
  weaponOptions?: Ref[];
};

export type ArmorRoll<T> = {
  type: T;
  min: number;
  max: number;
};

export type AnyArmorRoll =
  | ArmorRoll<"CRIT_FAIL">
  | ArmorRoll<"FAIL">
  | ArmorRoll<"SAVE">;

export type ArmorProfileData = {
  id: string;
  name: string;
  rolls: AnyArmorRoll[];
  tier?: EquipmentTier;
  effects?: EffectData[];
};

export type DefenseProfileData = {
  id: string;
  name: string;
};

export type ActionData = {
  id: string;
  name: string;
  description: string;
  isDefaultAction: boolean;
  unit?: "INCHES";
  hasClashTest?: boolean;
};

export type InitiativeStepData = {
  id: string;
  name: string;
  value: number | "ANY";
  actions: ActionData[];
};

export type ShieldProfileData = {
  id: string;
  name: string;
  effects?: EffectData[];
};

type EquipmentTier = "LEGENDARY";

export type ItemProfileType =
  | "WORN"
  | "USABLE"
  | "CONSUMABLE_INSTANT"
  | "CONSUMABLE_BUFF"
  | "CONSUMABLE_TOSSED";

export type ItemProfileData = {
  id: string;
  name: string;
  type: ItemProfileType;
  tier?: EquipmentTier;
  effects?: EffectData[];
};

export type UpgradeProfileData = {
  id: string;
  name: string;
  type: "WEAPON_UPGRADE" | "ARMOR_UPGRADE" | "SHIELD_UPGRADE";
  tier?: EquipmentTier;
  effects?: EffectData[];
};

export type DefaultKnight = Pick<
  Greathelm.KnightData,
  | "name"
  | "profile"
  | "armor"
  | "weapons"
  | "shields"
  | "items"
  | "wearables"
  | "upgrades"
>;

export type RulesetData = {
  version: string;

  maxQuickBattleKnights: number;
  maxQuickBattleWornAndUsables: number;
  maxQuickBattleConsumables: number;

  weaponTraits: WeaponTraitData[];
  defenses: DefenseProfileData[];
  initiativeSteps: InitiativeStepData[];
  defaultKnight: DefaultKnight;
  knightProfiles: KnightProfileData[];
  weaponProfiles: WeaponProfileData[];
  armorProfiles: ArmorProfileData[];
  shieldProfiles: ShieldProfileData[];
  itemProfiles: ItemProfileData[];
  wearableProfiles: ItemProfileData[];
  upgradeProfiles: UpgradeProfileData[];
};

/**
 * Encapsulates the Greathelm ruleset.
 *
 * All ruleset data are exposed via cached getters, using WeakMaps with source
 * data as keys. If you need to bust the cache, create new data values at the
 * appropriate keys instead of mutating them.
 *
 * Engine classes should generally be treated as immutable structures, so only
 * do this if you have a good reason.
 */
export class Ruleset {
  data: RulesetData;
  cache: WeakMapCache;

  constructor(data: RulesetData) {
    this.data = data;
    this.cache = new WeakMapCache();
  }

  get version() {
    return this.data.version;
  }

  get defaultKnight() {
    return this.data.defaultKnight;
  }

  get maxQuickBattleKnights() {
    return this.data.maxQuickBattleKnights;
  }

  get maxQuickBattleWornAndUsables() {
    return this.data.maxQuickBattleWornAndUsables;
  }

  get maxQuickBattleConsumables() {
    return this.data.maxQuickBattleConsumables;
  }

  get defenses() {
    return this.cache.get(this.data.defenses, (arr) =>
      Lookup.fromItems(arr.map((p) => new DefenseProfile(this, p))),
    );
  }

  get weaponTraits() {
    return this.cache.get(this.data.weaponTraits, (arr) =>
      Lookup.fromItems(arr),
    );
  }

  get initiativeSteps() {
    return this.cache.get(this.data.initiativeSteps, (arr) =>
      Lookup.fromItems(arr),
    );
  }

  get actions() {
    return this.cache.get(this.initiativeSteps, (lookup) =>
      Lookup.fromItems(lookup.toArray().flatMap((step) => step.actions)),
    );
  }

  get defaultActions() {
    return this.cache.get(this.actions, (lookup) =>
      lookup.toArray().filter((action) => action.isDefaultAction),
    );
  }

  get knightProfiles() {
    return this.cache.get(this.data.knightProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new KnightProfile(this, p))),
    );
  }

  get weaponProfiles() {
    return this.cache.get(this.data.weaponProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new WeaponProfile(this, p))),
    );
  }

  get armorProfiles() {
    return this.cache.get(this.data.armorProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new ArmorProfile(this, p))),
    );
  }

  get shieldProfiles() {
    return this.cache.get(this.data.shieldProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new ShieldProfile(this, p))),
    );
  }

  get itemProfiles() {
    return this.cache.get(this.data.itemProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new ItemProfile(this, p))),
    );
  }

  get wearableProfiles() {
    return this.cache.get(this.data.wearableProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new ItemProfile(this, p))),
    );
  }

  get upgradeProfiles() {
    return this.cache.get(this.data.upgradeProfiles, (arr) =>
      Lookup.fromItems(arr.map((p) => new UpgradeProfile(this, p))),
    );
  }
}
